import { notification } from 'antd';
import type { ClipboardEvent } from 'react';
import type { FormInstance } from 'antd/es/form';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr';

export const LEAVE_TYPE_LABELS: Record<string, string> = {
    ANNUAL_PAID: 'Yıllık Ücretli İzin',
    SICK_WITH_REPORT: 'Raporlu Hastalık İzni',
    UNPAID: 'Ücretsiz İzin',
    EXCUSE: 'Mazeret İzni',
    MARRIAGE: 'Evlilik İzni',
    MATERNITY: 'Doğum İzni',
    NURSING: 'Süt İzni',
    PATERNITY: 'Babalık İzni',
    ADOPTION: 'Evlat Edinme İzni',
    BEREAVEMENT: 'Ölüm (Vefat) İzni',
    JOB_SEARCH: 'Yeni İş Arama İzni',
};

export const COMPANY_INFO_WARNING_MESSAGE =
    'Vergi Numarası, Vergi Dairesi ve Fatura Adresi bilgileri eksiktir. Lütfen doldurunuz.';

export const getLeaveTypeLabel = (rawType: unknown): string => {
    if (typeof rawType === 'string' && rawType.length) {
        return LEAVE_TYPE_LABELS[rawType] ?? rawType;
    }
    if (rawType && typeof (rawType as any)?.name === 'string') {
        return (rawType as any).name;
    }
    return 'İzin';
};

type NotificationType = 'success' | 'info' | 'warning' | 'error';
export const openNotification = (type: NotificationType, status: string, description: any, duration: number = 2) => {
    const safeDescription =
        typeof description === 'object'
            ? JSON.stringify(description)
            : description;
    notification.destroy();
    const args = {
        message: status,
        description: safeDescription,
        duration: duration,
    };
    notification[type](args);
};

const INVALID_TOKEN_MESSAGE = 'The specified token is not valid.';
const INVALID_TOKEN_TR_MESSAGE = 'Oturumunuz geçersiz veya süresi dolmuş. Lütfen tekrar giriş yapın.';
const POST_LOGIN_REDIRECT_PATH_KEY = 'postLoginRedirectPath';
const POST_LOGIN_REDIRECT_OWNER_ID_KEY = 'postLoginRedirectOwnerId';
const POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY = 'postLoginRedirectOwnerEmail';
const POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY = 'postLoginRedirectOwnerSubject';
const POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY = 'postLoginRedirectRequiresSameUser';

type PostLoginRedirectState = {
    path: string;
    ownerId?: string | null;
    ownerEmail?: string | null;
    ownerSubject?: string | null;
    requiresSameUser?: boolean;
    allowAnonymous?: boolean;
};

type PostLoginRedirectUser = {
    id?: unknown;
    accountId?: unknown;
    account_id?: unknown;
    email?: unknown;
    username?: unknown;
    subject?: unknown;
    accessToken?: unknown;
};

type PostLoginRedirectIdentity = {
    id: string | null;
    email: string | null;
    subject: string | null;
};

export const isInvalidTokenMessage = (message: unknown): boolean =>
    typeof message === 'string' && message.toLocaleLowerCase('tr-TR').includes(INVALID_TOKEN_MESSAGE.toLocaleLowerCase('tr-TR'));

const isPostLoginRedirectAllowed = (path: string): boolean => {
    if (!path.startsWith('/')) {
        return false;
    }
    if (path.startsWith('//')) {
        return false;
    }
    return ![
        '/login',
        '/register',
        '/forgot-password',
        '/forgot-password-confirm',
        '/email-verify-check',
        '/oauth2/google/redirect-uri',
        '/oauth2/linkedin/redirect-uri',
        '/email-verification',
        '/email-change-verification',
    ].some((prefix) => path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}#`));
};

const normalizeStorageValue = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const normalized = String(value).trim();
    if (!normalized.length || ['undefined', 'null', 'nan'].includes(normalized.toLocaleLowerCase('tr-TR'))) {
        return null;
    }
    return normalized;
};

const normalizeEmailValue = (value: unknown): string | null => {
    const normalized = normalizeStorageValue(value);
    return normalized ? normalized.toLocaleLowerCase('tr-TR') : null;
};

const normalizeSubjectValue = (value: unknown): string | null => {
    const normalized = normalizeStorageValue(value);
    return normalized ? normalized.toLocaleLowerCase('tr-TR') : null;
};

const decodeBase64Url = (value: string): string | null => {
    try {
        const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
        const decoded = window.atob(padded);
        try {
            return decodeURIComponent(
                decoded
                    .split('')
                    .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
                    .join('')
            );
        } catch {
            return decoded;
        }
    } catch {
        return null;
    }
};

const readAccessTokenPayload = (token: unknown): Record<string, any> => {
    const normalizedToken = normalizeStorageValue(token);
    if (!normalizedToken) {
        return {};
    }

    const tokenParts = normalizedToken.split('.');
    if (tokenParts.length < 2) {
        return {};
    }

    const decodedPayload = decodeBase64Url(tokenParts[1]);
    if (!decodedPayload) {
        return {};
    }

    try {
        const parsed = JSON.parse(decodedPayload);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const resolvePostLoginRedirectIdentity = (user?: PostLoginRedirectUser): PostLoginRedirectIdentity => {
    const tokenPayload = readAccessTokenPayload(user?.accessToken ?? localStorage.getItem('accessToken'));
    const subject = normalizeSubjectValue(
        user?.subject
        ?? user?.username
        ?? tokenPayload.sub
        ?? tokenPayload.username
        ?? tokenPayload.preferred_username
        ?? tokenPayload.user_name
    );

    return {
        id: normalizeStorageValue(
            user?.id
            ?? user?.accountId
            ?? user?.account_id
            ?? tokenPayload.accountId
            ?? tokenPayload.account_id
            ?? tokenPayload.userId
            ?? tokenPayload.user_id
            ?? tokenPayload.id
            ?? localStorage.getItem('id')
        ),
        email: normalizeEmailValue(
            user?.email
            ?? tokenPayload.email
            ?? tokenPayload.mail
            ?? tokenPayload.upn
            ?? localStorage.getItem('email')
        ),
        subject,
    };
};

const hasIdentityValue = (identity: PostLoginRedirectIdentity): boolean =>
    Boolean(identity.id || identity.email || identity.subject);

const isPostLoginRedirectIdentityMatch = (
    ownerIdentity: PostLoginRedirectIdentity,
    currentIdentity: PostLoginRedirectIdentity,
    requiresSameUser: boolean,
): boolean => {
    let hasPositiveMatch = false;

    if (ownerIdentity.id) {
        if (!currentIdentity.id || ownerIdentity.id !== currentIdentity.id) {
            return false;
        }
        hasPositiveMatch = true;
    }

    if (ownerIdentity.email) {
        if (!currentIdentity.email || ownerIdentity.email !== currentIdentity.email) {
            return false;
        }
        hasPositiveMatch = true;
    }

    if (ownerIdentity.subject) {
        if (!currentIdentity.subject || ownerIdentity.subject !== currentIdentity.subject) {
            return false;
        }
        hasPositiveMatch = true;
    }

    if (requiresSameUser) {
        return hasPositiveMatch;
    }

    return hasPositiveMatch || !hasIdentityValue(ownerIdentity);
};

const readPostLoginRedirectState = (storedValue: string | null): PostLoginRedirectState | null => {
    if (!storedValue) {
        return null;
    }

    try {
        const parsed = JSON.parse(storedValue);
        if (parsed && typeof parsed.path === 'string') {
            return {
                path: parsed.path,
                ownerId: normalizeStorageValue(parsed.ownerId),
                ownerEmail: normalizeEmailValue(parsed.ownerEmail),
                ownerSubject: normalizeSubjectValue(parsed.ownerSubject),
                requiresSameUser: parsed.requiresSameUser === true,
                allowAnonymous: parsed.allowAnonymous === true,
            };
        }
    } catch {
        return null;
    }

    return null;
};

export const rememberCurrentPathForPostLoginRedirect = () => {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!isPostLoginRedirectAllowed(path)) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY);
        return;
    }
    const ownerIdentity = resolvePostLoginRedirectIdentity({
        id: sessionStorage.getItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY),
        email: sessionStorage.getItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY),
        subject: sessionStorage.getItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY),
    });
    const requiresSameUser = sessionStorage.getItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY) === 'true';
    sessionStorage.setItem(POST_LOGIN_REDIRECT_PATH_KEY, JSON.stringify({
        path,
        ownerId: ownerIdentity.id,
        ownerEmail: ownerIdentity.email,
        ownerSubject: ownerIdentity.subject,
        requiresSameUser,
        allowAnonymous: !requiresSameUser && !hasIdentityValue(ownerIdentity),
    }));
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY);
};

export const getPostLoginRedirectPath = (fallbackPath: string = '/', user?: PostLoginRedirectUser) => {
    const redirectState = readPostLoginRedirectState(sessionStorage.getItem(POST_LOGIN_REDIRECT_PATH_KEY));
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY);

    if (!redirectState || !isPostLoginRedirectAllowed(redirectState.path)) {
        return fallbackPath;
    }

    const ownerIdentity = {
        id: redirectState.ownerId ?? null,
        email: redirectState.ownerEmail ?? null,
        subject: redirectState.ownerSubject ?? null,
    };
    if (!hasIdentityValue(ownerIdentity) && redirectState.requiresSameUser !== true) {
        return redirectState.allowAnonymous === true ? redirectState.path : fallbackPath;
    }

    const currentIdentity = resolvePostLoginRedirectIdentity(user);
    if (!isPostLoginRedirectIdentityMatch(ownerIdentity, currentIdentity, redirectState.requiresSameUser === true)) {
        return fallbackPath;
    }

    return redirectState.path;
};

export const redirectToLoginWithHardReload = () => {
    if (window.location.pathname === '/login') {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY);
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY);
        window.location.reload();
        return;
    }

    rememberCurrentPathForPostLoginRedirect();
    window.location.replace('/login');
};

export const notifyInvalidTokenAndLogout = () => {
    openNotification('error', 'Oturum Hatası', INVALID_TOKEN_TR_MESSAGE, 3);
    clearStorage();
    redirectToLoginWithHardReload();
};

export const extractApiErrorMessage = (error: any, fallback: string) => {
    try {
        const responseData = error?.response?.data;
        if (responseData) {
            if (typeof responseData === 'string') {
                try {
                    const parsed = JSON.parse(responseData);
                    if (isInvalidTokenMessage(parsed?.message)) return INVALID_TOKEN_TR_MESSAGE;
                    if (parsed?.message) return parsed.message;
                } catch {
                    if (isInvalidTokenMessage(responseData)) return INVALID_TOKEN_TR_MESSAGE;
                    if (responseData.trim().length) return responseData;
                }
            }
            if (typeof responseData === 'object' && responseData.message) {
                if (isInvalidTokenMessage(responseData.message)) return INVALID_TOKEN_TR_MESSAGE;
                return responseData.message;
            }
        }

        if (typeof error?.message === 'string' && error.message.length) {
            try {
                const parsed = JSON.parse(error.message);
                if (isInvalidTokenMessage(parsed?.message)) return INVALID_TOKEN_TR_MESSAGE;
                if (parsed?.message) return parsed.message;
            } catch {
                if (isInvalidTokenMessage(error.message)) return INVALID_TOKEN_TR_MESSAGE;
                return error.message;
            }
        }
    } catch {
        // fall through to fallback
    }
    return fallback;
};

// Show a generic notification when AntD Form validation fails
export function notifyFormValidationFailed(
    info: any,
    fallbackMessage: string = 'Formda hatalar var. Lütfen kontrol edin.',
    useFirstError: boolean = true,
) {
    try {
        let message = fallbackMessage;
        if (useFirstError) {
            const firstError = info?.errorFields?.[0];
            const firstMsg = Array.isArray(firstError?.errors) && firstError.errors.length ? firstError.errors[0] : undefined;
            if (firstMsg) message = firstMsg;
        }
        openNotification('error', 'Hata', message);
    } catch {
        openNotification('error', 'Hata', fallbackMessage);
    }
}

export function isAuthorized(requiredPermission: any) {
    let permissions = localStorage.getItem('permissions')
    return permissions?.includes(requiredPermission);
}

export function isFutureDate(dateString: string): boolean {
    const inputDate = dayjs(dateString);
    const now = dayjs();

    // Gelen tarih ve saat şimdiki zamandan büyük mü kontrol et
    return inputDate.isAfter(now);
}

export function formatTime(date: string): string {
    if (!date) {
        return '';
    }
    return dayjs(date).format('HH:mm');
}

export function formatDateString(date: string): string {
    if (!date) {
        return '';
    }
    return dayjs(date).format('DD/MM/YYYY');
}

export function formatDate(date: string): string {
    if (!date) {
        return '';
    }
    return dayjs(date).format('DD/MM/YYYY HH:mm');
}

export function getDateRangeDefault(): any {
    let dateRangeDefaultValue: [dayjs.Dayjs, dayjs.Dayjs] = [
        dayjs().add(-90, 'd'),
        dayjs()
    ];
    return dateRangeDefaultValue;
}

export function getCurrentMonthDateRange() {
    let dateRangeDefaultValue: [dayjs.Dayjs, dayjs.Dayjs] = [
        dayjs().add(-7, 'd'), dayjs()
    ];

    return dateRangeDefaultValue;
}

export function getCurrentMonthDateRangeNew() {

    let dateRangeDefaultValue: [dayjs.Dayjs, dayjs.Dayjs] = [
        dayjs().startOf('month'), dayjs()
    ];

    return dateRangeDefaultValue;
}

export function convertDateFormat(dateStr: any): string {
    return dayjs(dateStr, 'DD/MM/YYYY').format('YYYY-MM-DD');
}

export function filterOption(input: string, option: any): boolean {
    return option?.children.toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'));
}

export const toTurkishUpper = (value?: string | null) => {
    if (typeof value !== 'string') {
        return value;
    }
    return value.toLocaleUpperCase('tr-TR');
};

const hasInactiveFlag = (value: any): boolean =>
    value?.enabled === false || value?.status === false || value?.isActive === false;

export const isAccountActive = (entity: any): boolean => {
    if (entity == null) {
        return false;
    }
    if (typeof entity !== 'object') {
        return true;
    }
    if (hasInactiveFlag(entity)) {
        return false;
    }
    if (entity?.account && typeof entity.account === 'object' && hasInactiveFlag(entity.account)) {
        return false;
    }
    return true;
};

export function formatToTL(value: number): string {
    const formattedValue = new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
    }).format(value);

    return `${formattedValue.replace('₺', '').trim()} ₺`;
}

export function formatToEUR(value: number): string {
    const formattedValue = new Intl.NumberFormat('eu-EU', {
        style: 'currency',
        currency: 'EUR',
    }).format(value);

    return `${formattedValue.replace('€', '').trim()} €`;
}

export function formatToUSD(value: number): string {
    const formattedValue = new Intl.NumberFormat('us-US', {
        style: 'currency',
        currency: 'USD',
    }).format(value);

    return `${formattedValue.replace('$', '').trim()} $`;
}


export function formatRelativeTime(date: string): string {
    dayjs.extend(relativeTime);
    dayjs.locale('tr');
    if (!date) {
        return '';
    }

    const inputDate = dayjs(date);
    const now = dayjs();

    return inputDate.from(now);
}

export const capitalizeWords = (str: any) => {
    return str;
    if (!str) return '';

    let result = str.toLocaleLowerCase('tr-TR');

    result = result.replace(/(?:^|\s)(\S)/g, (match: string, firstChar: string) => {
        return match.replace(firstChar, firstChar.toLocaleUpperCase('tr-TR'));
    });

    return result;
};


export const capitalizeSentences = (str: any) => {
    return str;
    if (!str) return '';
    let result = str.toLocaleLowerCase('tr-TR');
    result = result.charAt(0).toLocaleUpperCase('tr-TR') + result.slice(1);
    result = result.replace(/(\.\s*)(\w)/g, (match: any, separator: any, char: any) => {
        return separator + char.toLocaleLowerCase('tr-TR');
    });

    return result;
};

export enum TransportationType {
    DRIVER = 'DRIVER',
    TAXI = 'TAXI',
    PUBLIC_TRANSPORT = 'PUBLIC_TRANSPORT',
    PASSENGER = 'PASSENGER',
    ON_FOOT = 'ON_FOOT',
}

export const TRANSPORTATION_TYPE_LABELS: Record<TransportationType, string> = {
    [TransportationType.DRIVER]: 'Sürücü',
    [TransportationType.TAXI]: 'Taksi',
    [TransportationType.PUBLIC_TRANSPORT]: 'Toplu Taşıma',
    [TransportationType.PASSENGER]: 'Yolcu',
    [TransportationType.ON_FOOT]: 'Yaya',
};

export const TRANSPORTATION_TYPE_OPTIONS = Object.values(TransportationType).map((value) => ({
    label: TRANSPORTATION_TYPE_LABELS[value],
    value,
}));

export const PARAMETER_UNIT_OPTIONS = [
  { value: 'ad', label: 'Adet' },
  { value: 'b', label: 'Bölme' },
  { value: 'hL', label: 'Hektolitre' },
  { value: 'L', label: 'Litre' },
  { value: 'dL', label: 'Desilitre' },
  { value: 'cL', label: 'Santilitre' },
  { value: 'mL', label: 'Mililitre' },
  { value: 'gal', label: 'Galon' },
  { value: 'bbl', label: 'Varil' },

  { value: 'km', label: 'Kilometre' },
  { value: 'hm', label: 'Hektometre' },
  { value: 'dam', label: 'Dekametre' },
  { value: 'm', label: 'Metre' },
  { value: 'dm', label: 'Desimetre' },
  { value: 'cm', label: 'Santimetre' },
  { value: 'mm', label: 'Milimetre' },

  { value: 'km2', label: 'Kilometrekare' },
  { value: 'hm2', label: 'Hektometrekare' },
  { value: 'dam2', label: 'Dekametrekare' },
  { value: 'm2', label: 'Metrekare' },
  { value: 'dm2', label: 'Desimetrekare' },
  { value: 'cm2', label: 'Santimetrekare' },
  { value: 'mm2', label: 'Milimetrekare' },

  { value: 'm3', label: 'Metreküp' },
  { value: 'dm3', label: 'Desimetreküp' },
  { value: 'cm3', label: 'Santimetreküp' },
  { value: 'mm3', label: 'Milimetreküp' },

  { value: 't', label: 'Ton' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'hg', label: 'Hektogram' },
  { value: 'dag', label: 'Dekagram' },
  { value: 'g', label: 'Gram' },
  { value: 'dg', label: 'Desigram' },
  { value: 'cg', label: 'Santigram' },
  { value: 'mg', label: 'Miligram' },

  { value: 'mtul', label: 'Metretül' },
];

export const PARAMETER_UNIT_LABELS = PARAMETER_UNIT_OPTIONS.reduce<Record<string, string>>((acc, option) => {
    acc[String(option.value)] = option.label;
    return acc;
}, {});

export const PARAMETER_PERIOD_OPTIONS = [
    { value: 1, label: '1 Ay' },
    { value: 2, label: '2 Ay' },
    { value: 3, label: '3 Ay' },
    { value: 4, label: '4 Ay' },
    { value: 6, label: '6 Ay' },
    { value: 12, label: '12 Ay' },
    { value: 24, label: '2 Yıl' },
    { value: 36, label: '3 Yıl' },
    { value: 48, label: '4 Yıl' },
    { value: 60, label: '5 Yıl' },
    { value: 72, label: '6 Yıl' },
    { value: 84, label: '7 Yıl' },
    { value: 96, label: '8 Yıl' },
    { value: 108, label: '9 Yıl' },
    { value: 120, label: '10 Yıl' },
];

export const PARAMETER_PERIOD_LABELS = PARAMETER_PERIOD_OPTIONS.reduce<Record<string, string>>((acc, option) => {
    acc[String(option.value)] = option.label;
    return acc;
}, {});

export const PARAMETER_UNIT_DEFAULT = 'ad';
export const PARAMETER_PERIOD_DEFAULT = 12;

const formatProposalNumericValue = (value: number): string => {
    if (!Number.isFinite(value)) {
        return '-';
    }

    const formatted = new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);

    return formatted;
};

export const resolveParameterUnitLabel = (unit?: string | null): string => {
    if (!unit) {
        return PARAMETER_UNIT_LABELS[PARAMETER_UNIT_DEFAULT];
    }

    return PARAMETER_UNIT_LABELS[String(unit)] ?? String(unit);
};

export const formatDurationMonths = (months?: number | null): string => {
    const numericMonths = Number(months);
    if (!Number.isFinite(numericMonths) || numericMonths <= 0) {
        return '-';
    }

    if (Number.isInteger(numericMonths) && PARAMETER_PERIOD_LABELS[String(numericMonths)]) {
        return PARAMETER_PERIOD_LABELS[String(numericMonths)];
    }

    return `${formatProposalNumericValue(numericMonths)} Ay`;
};

export const formatDerivedPeriod = (
    maxDurationMonths?: number | null,
    controlCount?: number | null,
): string => {
    const duration = Number(maxDurationMonths);
    const count = Number(controlCount);

    if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(count) || count <= 0) {
        return '-';
    }

    return formatDurationMonths(duration / count);
};

export const formatQuantityWithUnit = (
    quantity?: number | string | null,
    unit?: string | null,
): string => {
    if (quantity === null || quantity === undefined || quantity === '') {
        return '-';
    }

    const numericQuantity = Number(quantity);
    const quantityText = Number.isFinite(numericQuantity)
        ? formatProposalNumericValue(numericQuantity)
        : String(quantity);

    return `${quantityText} ${resolveParameterUnitLabel(unit)}`.trim();
};

export const clearStorage = () => {
    const currentIdentity = resolvePostLoginRedirectIdentity();
    if (currentIdentity.id) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_OWNER_ID_KEY, currentIdentity.id);
    }
    if (currentIdentity.email) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_OWNER_EMAIL_KEY, currentIdentity.email);
    }
    if (currentIdentity.subject) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_OWNER_SUBJECT_KEY, currentIdentity.subject);
    }
    sessionStorage.setItem(POST_LOGIN_REDIRECT_REQUIRES_SAME_USER_KEY, 'true');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessTokenExpiredAt');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiredAt');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('email');
    localStorage.removeItem('phoneNumber');
    localStorage.removeItem('id');
    localStorage.removeItem('roles');
    localStorage.removeItem('permissions');
    localStorage.removeItem('profilePhotoBase64');
    localStorage.removeItem('profilePhoto');
    localStorage.removeItem('profileCompletion');
    localStorage.removeItem('isPartner');
};

export const formatToTRY = (value: number, showCurrency: boolean = true): string => {
    if(!value){
        value = 0;
    }
    return new Intl.NumberFormat('tr-TR', {
        style: showCurrency ? 'currency' : 'decimal',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
}

export const normalizeTurkish = (text: string): string => {
    text = text.replaceAll('İ','i');
    return text
        .toLowerCase()
        .replace(/İ/g, 'i')
        .replace(/I/g, 'i')
        .replace(/ı/g, 'i')
        .replace(/Ğ/g, 'g')
        .replace(/ğ/g, 'g')
        .replace(/Ü/g, 'u')
        .replace(/ü/g, 'u')
        .replace(/Ş/g, 's')
        .replace(/ş/g, 's')
        .replace(/Ö/g, 'o')
        .replace(/ö/g, 'o')
        .replace(/Ç/g, 'c')
        .replace(/ç/g, 'c');
}

export const normalizePastedPhoneNumber = (value?: string | null): string => {
    const digits = (value || '').replace(/\D+/g, '');

    let normalized = digits.replace(/^0+/, '');
    normalized = normalized.replace(/^90/, '');
    normalized = normalized.replace(/^90/, '');

    if (normalized.length > 10) {
        normalized = normalized.slice(-10);
    }

    return normalized;
};

export const handlePhonePaste = (
    e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    options?: { form?: FormInstance; fieldName?: string; onNormalized?: (value: string) => void },
) => {
    e.preventDefault();
    const pasted = e.clipboardData?.getData('Text') ?? '';
    const normalized = normalizePastedPhoneNumber(pasted);

    if (options?.form && options.fieldName) {
        options.form.setFieldsValue({ [options.fieldName]: normalized });
    }
    if (options?.onNormalized) {
        options.onNormalized(normalized);
    }

    const target = e.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (target) {
        target.value = normalized;
        target.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

export const sortAuthorizedPersonsByName = <T extends { firstName?: string | null; lastName?: string | null }>(
    list: T[] | null | undefined,
): T[] => {
    if (!Array.isArray(list)) {
        return [];
    }

    const getFullName = (person: T) => {
        const first = (person?.firstName ?? '').toString().trim();
        const last = (person?.lastName ?? '').toString().trim();
        return `${first} ${last}`.trim();
    };

    return [...list].sort((a, b) => {
        const nameA = getFullName(a);
        const nameB = getFullName(b);

        if (!nameA && !nameB) return 0;
        if (!nameA) return 1;
        if (!nameB) return -1;

        return nameA.localeCompare(nameB, 'tr', { sensitivity: 'base' });
    });
};

const PHONE_DIGIT_REGEX = /\d+/g;

export const normalizePhoneNumber = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const digits = (value.match(PHONE_DIGIT_REGEX) || []).join('');

    if (!digits) {
        return '';
    }

    let normalized = digits;

    if (normalized.startsWith('90') && normalized.length >= 12) {
        normalized = normalized.slice(-10);
    }

    if (normalized.length > 10) {
        normalized = normalized.slice(-10);
    }

    if (!normalized.startsWith('0')) {
        normalized = `0${normalized}`;
    }

    return normalized;
};

export const formatPhoneNumberDisplay = (value?: string | null): string => {
    const normalized = normalizePhoneNumber(value);

    if (normalized.length !== 11) {
        return normalized;
    }

    return normalized.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
};

export const formatPhoneNumberCopy = (value?: string | null): string => {
    return normalizePhoneNumber(value);
};

export const turkishLowerCase = (text: string): string => {
    if (!text) return '';

    return text
        // Büyük harfler
        .replace(/İ/g, 'i')
        .replace(/I/g, 'ı')
        .replace(/Ğ/g, 'ğ')
        .replace(/Ü/g, 'ü')
        .replace(/Ş/g, 'ş')
        .replace(/Ö/g, 'ö')
        .replace(/Ç/g, 'ç')
        // Küçük harfler
        .toLowerCase()
        // Normalize etmek için (isteğe bağlı)
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'ğ')
        .replace(/ü/g, 'ü')
        .replace(/ş/g, 'ş')
        .replace(/ö/g, 'ö')
        .replace(/ç/g, 'ç');
};

const EMAIL_TIME_MINUTE_STEP = 5;

const getMinuteCutoff = (minute: number) =>
    Math.ceil((minute + 1) / EMAIL_TIME_MINUTE_STEP) * EMAIL_TIME_MINUTE_STEP;

const roundToNextMinuteStep = (date: Dayjs) => {
    const minuteCutoff = getMinuteCutoff(date.minute());
    if (minuteCutoff >= 60) {
        return date.add(1, 'hour').minute(0).second(0).millisecond(0);
    }
    return date.minute(minuteCutoff).second(0).millisecond(0);
};

export const getDefaultEmailSendAt = (): Dayjs => {
    const now = dayjs();
    const nineAm = now.startOf('day').hour(9).minute(0).second(0).millisecond(0);
    if (now.isBefore(nineAm)) {
        return nineAm;
    }
    return roundToNextMinuteStep(now);
};

export const isEmailSendDateDisabled = (current?: Dayjs): boolean => {
    if (!current) {
        return false;
    }
    return current.isBefore(dayjs().startOf('day'), 'day');
};

type EmailSendDisabledTimeConfig = {
    disabledHours?: () => number[];
    disabledMinutes?: (selectedHour?: number) => number[];
    disabledSeconds?: () => number[];
};

export const getEmailSendDisabledTime = (current?: Dayjs): EmailSendDisabledTimeConfig => {
    if (!current) {
        return {};
    }
    const now = dayjs();
    if (!current.isSame(now, 'day')) {
        return {};
    }

    const minuteCutoff = getMinuteCutoff(now.minute());
    const disableCurrentHour = minuteCutoff >= 60;
    const minHourToHide = disableCurrentHour ? now.hour() + 1 : now.hour();
    const hoursToDisable = Array.from({ length: minHourToHide }, (_, idx) => idx);

    const disabledMinutes = (selectedHour?: number) => {
        if (selectedHour === undefined) {
            return [];
        }
        if (disableCurrentHour && selectedHour === now.hour()) {
            return Array.from({ length: 60 / EMAIL_TIME_MINUTE_STEP }, (_, idx) => idx * EMAIL_TIME_MINUTE_STEP);
        }
        if (selectedHour === now.hour()) {
            const minutes: number[] = [];
            for (let minute = 0; minute < minuteCutoff; minute += EMAIL_TIME_MINUTE_STEP) {
                minutes.push(minute);
            }
            return minutes;
        }
        return [];
    };

    return {
        disabledHours: () => hoursToDisable,
        disabledMinutes,
        disabledSeconds: () => [],
    };
};

type Role = {
    name: string;
    [key: string]: any;
}

export const hasRole = (roleName: string): boolean => {
    const rolesStr = localStorage.getItem('roles') || '[]';
    let roles: Role[] = [];

    try {
        roles = JSON.parse(rolesStr);
    } catch {
        roles = [];
    }

    return roles.some(role => role.name === roleName);
}
